import React, { useEffect, useState } from "react";
import {
  Table,
  TableRow,
  TableBody,
  TableHead,
  TableCell,
  useTheme,
  Box,
  TableContainer,
  Button,
  CircularProgress,
  Typography,
  Badge,
  IconButton,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import NewspaperIcon from "@mui/icons-material/Newspaper";
import NewsPopup from "../../../Popup/NewsPopup";
import {
  TABLE_HEAD_SIGNALS_CURRENT as TABLE_HEAD,
  applySortFilter,
  getComparator,
  hashInvestment,
  investArr,
} from "../../../../../component/Methods";
import VantageListHead from "../../../../../component/ListHead/SignalList";
import {
  sendWebSocketMessage,
  webSocketService,
} from "../../../../../hooks/useWebSocket";

const CurrentList = ({
  strategyValObj,
  strategies,
  strategyVal,
  setPasteButton,
  onCopyClick, // Add this prop
  isLoadingPerformance,
  setIsLoadingPerformance,
  hasFetchedPerformance,
  setHasFetchedPerformance,
  fetchPerformanceData,
  performanceData,
  isLoadingNews,
  setIsLoadingNews,
  hasFetchedNews,
  setHasFetchedNews,
  fetchNewsData,
  newsData,
}) => {
  const [data, setData] = useState([]);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("value");
  const [newsPopupOpen, setNewsPopupOpen] = useState(false);
  const [selectedNewsData, setSelectedNewsData] = useState([]);
  const [selectedTicker, setSelectedTicker] = useState("");

 
  useEffect(() => {
    if (strategyValObj && strategies && strategyVal) {
      const strategy = strategies.find(
        (strategy) => strategy.strategyId === strategyValObj
      );

      if (strategy && strategy.signals) {
        const { weights_keys, weights_values } = strategy.signals;

        const keys = Object.keys(weights_keys);
        const combinedArray = keys.map((key) => {
          const investment = weights_keys[key]?.investment;
          const symbol = weights_keys[key]?.symbol;
          const stock = weights_keys[key]?.stock;
          const name = stock ? `${symbol}/${stock}` : symbol;

          // Get performance data for this ticker

          // Get news data for this ticker
          const newsItems = newsData[symbol?.toUpperCase()];

          return {
            key,
            investment: investment,
            name: name?.toUpperCase() || "",
            value: (weights_values[key] * 100)?.toFixed(4),
            long_short: weights_values[key] >= 0 ? "L" : "S",
            stock: stock,
            news: newsItems,
            assetType:hashInvestment(investment) || "Equity",
          };
        });

        const filteredUsers = applySortFilter(
          combinedArray,
          getComparator(order, orderBy)
        );

        setData(filteredUsers);
      } else {
        setData([]);
      }
    } else {
      setData([]);
    }
  }, [
    strategies,
    strategyValObj,
    strategyVal,
    order,
    orderBy,
    performanceData,
    newsData,
  ]);

  // Separate useEffect to reset performance and news data only when strategy changes
  useEffect(() => {
    setHasFetchedPerformance(false);
    setIsLoadingPerformance(false);
    setHasFetchedNews(false);
    setIsLoadingNews(false);
  }, [strategyValObj]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleNewsClick = (newsItems, ticker) => {
    setSelectedNewsData(newsItems || []);
    setSelectedTicker(ticker);
    setNewsPopupOpen(true);
  };

  const handleNewsPopupClose = () => {
    setNewsPopupOpen(false);
    setSelectedNewsData([]);
    setSelectedTicker("");
  };

  const theme = useTheme();

  return (
    <>
      <Box
        sx={{
          borderRadius: "4px",
          marginTop: "10px",
          width: "100%",
          marginRight: 1,
        }}
      >
        {/* Hidden data element for parent component to access */}
        <div
          data-testid="current-list-data"
          data-json-data={JSON.stringify(data)}
          style={{ display: "none" }}
        />

        <TableContainer>
          <Table
            size="small"
            stickyHeader
            aria-label="a dense table"
            sx={{
              height: "max-content",
            }}
          >
            <VantageListHead
              order={order}
              orderBy={orderBy}
              headLabel={TABLE_HEAD}
              onRequestSort={handleRequestSort}
              noStatus={true}
              customizeFont={12}
            />
            <TableBody>
              {data?.map((row, index) => {
                const ticker = row.name.split("/")[0];

                return (
                  <TableRow
                    key={row?.key}
                    sx={{
                      overflow: "auto",
                      "&:hover": {
                        background: theme.palette.grey[200],
                        cursor: "pointer",
                      },
                      "& > .MuiTableCell-root": {
                        paddingTop: "1px",
                        paddingBottom: "0px !important",
                        lineHeight: "1.2",
                      },
                    }}
                  >
                    <TableCell
                      align="left"
                      sx={{
                        fontSize: "12px",
                        paddingLeft: "5px",
                        color: theme.palette.text.primary,
                        borderRight: "none",
                        cursor: "pointer",
                      }}
                    >
                      {row?.name}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontSize: "12px",
                        paddingLeft: "5px",
                        color: theme.palette.text.primary,
                        borderLeft: "none",
                        borderRight: "none",
                      }}
                    >
                      {row?.value}%
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        fontSize: "12px",
                        paddingLeft: "10px",
                        color: theme.palette.text.primary,
                        borderLeft: "none",
                        borderRight: "none",
                        height: "21.5px",
                      }}
                    >
                      {row?.long_short}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        fontSize: "12px",
                        paddingLeft: "10px",
                        color: theme.palette.text.primary,
                        borderLeft: "none",
                        borderRight: "none",
                        height: "21.5px",
                      }}
                    >
                      {row?.assetType}
                    </TableCell>

                    {/* News column */}
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: "12px",
                        paddingLeft: "5px",
                        color: theme.palette.text.primary,
                        borderLeft: "none",
                        borderRight: "none",
                      }}
                    >
                      {hasFetchedNews && row.news && row.news.length > 0 && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.25,
                            cursor: "pointer",
                            height: "20px", // Fixed height
                            maxHeight: "20px",
                          }}
                          onClick={() => handleNewsClick(row.news, ticker)}
                        >
                          <IconButton
                            size="small"
                            sx={{
                              color: theme.palette.primary.main,
                              backgroundColor:
                                theme.palette.primary.light + "20",
                              borderRadius: "4px",
                              padding: "2px",
                              minWidth: "16px",
                              width: "16px",
                              height: "16px",
                              "&:hover": {
                                backgroundColor:
                                  theme.palette.primary.light + "40",
                                transform: "scale(1.05)",
                                boxShadow: `0 2px 8px ${theme.palette.primary.main}40`,
                              },
                              transition: "all 0.2s ease-in-out",
                              "& .MuiSvgIcon-root": {
                                fontSize: "12px",
                              },
                            }}
                          >
                            <NewspaperIcon fontSize="inherit" />
                          </IconButton>
                          <Box
                            sx={{
                              backgroundColor: theme.palette.error.main,
                              color: "white",
                              fontSize: "11px",
                              height: "12px",
                              minWidth: "12px",
                              borderRadius: "6px",
                              fontWeight: 600,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              padding: "0 2px",
                              boxShadow: `0 1px 2px rgba(0,0,0,0.2)`,
                            }}
                          >
                            {row.news.length}
                          </Box>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* News Popup */}
      <NewsPopup
        open={newsPopupOpen}
        onClose={handleNewsPopupClose}
        newsData={selectedNewsData}
        ticker={selectedTicker}
      />
    </>
  );
};
export default CurrentList;
